import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { DataViewEditBox, useForm } from '../../../../lib/components';
import { PayrollSystemView } from './system/view';
import { PayrollMappingView } from './mapping/view';
import { PayrollSystemEdit } from './system/form';
import { PayrollMappingEdit } from './mapping/form';
import { useState } from 'react';
import { PayrollExportMapping, CompanyExtended } from '@pxtra-client-lib-legacy/gen';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePxtraApiClient } from '../../../../hooks/usePxtraApiClient';
import { useStore } from '../../../../hooks/useStore';
import { ExportLogsView } from './exportLogs/view';
import { useCompanySettings } from '../../../../queries/company';

export const CompanyPayrollSettings = observer(() => {
  const { t } = useTranslation();
  const form = useForm();
  const apiClient = usePxtraApiClient();
  const queryClient = useQueryClient();
  const [canEditSystem, setCanEditSystem] = useState<boolean>(true);
  const [canEditMapping, setCanEditMapping] = useState<boolean>(true);
  const [tableData, setTableData] = useState<PayrollExportMapping | null>(null);

  const { companyStore } = useStore();
  const companyId = companyStore.company?.id ?? 'MISSING_ID';

  const { data: companySettings } = useCompanySettings(companyStore.company?.id ?? '');
  const selectedPayrollSystem = companySettings?.payroll_system ?? 'OTHER';

  const { mutate } = useMutation({
    mutationFn: ({
      data,
      company
    }: {
      data: Record<string, string>;
      company: CompanyExtended['id'];
    }) =>
      apiClient.companies.companiesSettingsPayrollPartialUpdate({
        id: company,
        requestBody: data
      }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['company', variables.company, 'settings', 'payroll']
      });
    }
  });

  return (
    <div className="flex flex-col gap-y-4 mb-4">
      <DataViewEditBox
        title={t('payroll:payrollSystem')}
        view={() => <PayrollSystemView />}
        edit={() => <PayrollSystemEdit formRef={form.formRef} />}
        onSubmit={form.handleSubmit}
        canEdit={canEditSystem}
        canSubmit
        onModeChanged={(mode) => {
          if (mode === 'edit') {
            setCanEditMapping(false);
          } else {
            setCanEditMapping(true);
          }
        }}
      />

      <div className="w-full flex flex-row gap-x-4">
        <div className="w-1/2 grow">
          <DataViewEditBox
            title={t('payroll:salaryTypeMapping')}
            view={() => <PayrollMappingView />}
            edit={() => <PayrollMappingEdit onDataChange={(data) => setTableData(data)} />}
            canEdit={canEditMapping}
            canSubmit
            onSubmit={() => {
              mutate({ data: tableData ?? {}, company: companyId });
            }}
            onModeChanged={(mode) => {
              if (mode === 'edit') {
                setCanEditSystem(false);
              } else {
                setCanEditSystem(true);
              }
            }}
          />
        </div>
        {['PERSONIO', 'PAYCHEX_PLUS'].includes(selectedPayrollSystem) && (
          <div className="w-1/2">
            <ExportLogsView />
          </div>
        )}
      </div>
    </div>
  );
});
